import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { openAgreementDoc } from "@/lib/agreementDoc";
import {
  AGREEMENT_VERSION, AGREEMENT_EFFECTIVE, AGREEMENT_INTRO,
  AGREEMENT_SECTIONS, AGREEMENT_SCHEDULES,
} from "@/config/agreement";

function Body({ body }) {
  return body.map((b, i) =>
    Array.isArray(b) ? (
      <ul key={i} className="list-disc pl-5 space-y-1 my-2 text-muted-foreground">
        {b.map((li, j) => <li key={j}>{li}</li>)}
      </ul>
    ) : (
      <p key={i} className="my-2 text-muted-foreground leading-relaxed">{b}</p>
    )
  );
}

// Full agreement text — reused on screen and for the printable copy.
function AgreementText() {
  return (
    <div className="agreement-text">
      <h2 className="font-serif text-3xl tracking-tighter">THE SPOTTERS — {AGREEMENT_INTRO.title}</h2>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
        Version {AGREEMENT_VERSION} · Effective {AGREEMENT_EFFECTIVE}
      </p>
      {AGREEMENT_INTRO.parties.map((p, i) => <p key={i} className="my-2 text-sm leading-relaxed">{p}</p>)}
      {AGREEMENT_SECTIONS.map((s) => (
        <section key={s.n} className="mt-6">
          <h3 className="font-serif text-xl tracking-tight">{s.n}. {s.title}</h3>
          <Body body={s.body} />
        </section>
      ))}
      {AGREEMENT_SCHEDULES.map((sc) => (
        <section key={sc.title} className="mt-6">
          <h3 className="font-serif text-xl tracking-tight">{sc.title}</h3>
          <table className="w-full text-sm mt-2 border border-foreground/20">
            <tbody>
              {sc.rows.map(([k, v], i) => (
                <tr key={i} className="border-b border-foreground/10">
                  <td className="py-2 px-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground align-top w-1/3">{k}</td>
                  <td className="py-2 px-3">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

export default function VendorAgreement({ gate = false, onAccepted }) {
  const [state, setState] = useState(null); // {version, accepted, acceptance}
  const [signer, setSigner] = useState("");
  const [designation, setDesignation] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try { setState((await api.get("/vendor/agreement")).data); }
    catch { setState({ error: true }); }
  };
  useEffect(() => { load(); }, []);

  const accept = async () => {
    if (!signer.trim()) return toast.error("Please enter the signatory's full name.");
    if (!agree) return toast.error("Please tick the confirmation box to accept.");
    setBusy(true);
    try {
      await api.post("/vendor/agreement/accept", { signer_name: signer, designation, agreed: true });
      toast.success("Agreement accepted — thank you.");
      await load();
      onAccepted && onAccepted();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadSigned = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/vendor/agreement/signed-pdf", { pdf: reader.result });
        toast.success("Signed copy uploaded.");
        await load();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  if (!state) return <div className="font-mono text-sm p-8">Loading agreement…</div>;
  const accepted = state.accepted;
  const acc = state.acceptance;

  return (
    <div className={gate ? "max-w-[820px] mx-auto px-6 md:px-12 py-14" : "max-w-[820px]"}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Legal · Vendor</p>
      <h1 className="font-serif text-5xl tracking-tighter">{gate && !accepted ? "One last step." : "Vendor Agreement."}</h1>
      {gate && !accepted && (
        <p className="mt-3 text-muted-foreground max-w-xl">
          Before your store goes live and you can list products, please review and accept The Spotters Vendor & Marketplace Agreement.
        </p>
      )}

      {/* Status / actions bar */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {accepted ? (
          <span className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] border border-[hsl(var(--accent))] px-3 py-1.5">
            ✓ Accepted · v{acc?.version}
          </span>
        ) : (
          <span className="font-mono text-[10px] uppercase tracking-widest text-destructive border border-destructive px-3 py-1.5">
            Not yet accepted · v{state.version}
          </span>
        )}
        <button onClick={() => openAgreementDoc(accepted ? acc : null)} className="font-mono text-[10px] uppercase tracking-widest border border-foreground px-3 py-1.5 hover:bg-foreground hover:text-background">Print / Save PDF</button>
      </div>

      {accepted && acc && (
        <div className="mt-4 border border-foreground/25 p-4 font-mono text-[11px] leading-relaxed">
          <p><span className="text-muted-foreground">Signed by:</span> {acc.signer_name}{acc.designation ? `, ${acc.designation}` : ""}</p>
          <p><span className="text-muted-foreground">On:</span> {acc.accepted_at ? new Date(acc.accepted_at).toLocaleString("en-IN") : "—"} · <span className="text-muted-foreground">Method:</span> {acc.method}</p>
          <div className="mt-2 flex gap-3">
            <button onClick={() => fileRef.current?.click()} className="underline hover:text-[hsl(var(--accent))]">
              {acc.signed_pdf ? "Replace signed copy" : "Upload a wet-signed copy (optional)"}
            </button>
            {acc.signed_pdf && <span className="text-[hsl(var(--accent))]">✓ Signed copy on file</span>}
            <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={uploadSigned} className="hidden" />
          </div>
        </div>
      )}

      {/* The agreement */}
      <div className="mt-8 border border-foreground p-6 md:p-8 max-h-[60vh] overflow-y-auto">
        <AgreementText />
      </div>

      {/* Accept form */}
      {!accepted && (
        <div className="mt-6 border border-foreground p-6 md:p-8 tag-punch pl-12">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Accept the agreement</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Signatory full name *</label>
              <input value={signer} onChange={(e) => setSigner(e.target.value)} className="underline-input w-full" placeholder="e.g. Rangoli Sharma" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Designation</label>
              <input value={designation} onChange={(e) => setDesignation(e.target.value)} className="underline-input w-full" placeholder="e.g. Proprietor / Director" />
            </div>
          </div>
          <label className="flex items-start gap-2.5 mt-5 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[hsl(var(--accent))]" />
            <span>I have read and agree to the Vendor & Marketplace Agreement (v{state.version}), and I am authorised to bind the Vendor to it.</span>
          </label>
          <button disabled={busy || !agree || !signer.trim()} onClick={accept}
            className="mt-5 bg-foreground text-background px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">
            {busy ? "Recording…" : "I Agree & Accept"}
          </button>
          <p className="mt-3 font-mono text-[10px] text-muted-foreground">Your acceptance is recorded with a timestamp and audit trail. You can also print and upload a wet-signed copy afterwards.</p>
        </div>
      )}
    </div>
  );
}
